import {
	useCallback,
	useContext
} from 'react';
import createDataContext from '../services/createDataContext';
import { mapLookupTypes } from '../services/utils';

import { getContactTags } from '../api/nora/contacts';
import {
	getAgencyOrganizations,
	getCredentialTypes,
	getContactTypes,
	getManagementOrganizations,
	getVendorOrganizations
} from '../api/nora/lookups';

import contactsReducer, {
	SET_AGENCIES,
	SET_CONTACT,
	SET_CONTACT_TAGS,
	SET_CONTACT_LIST,
	SET_CONTACT_TYPES,
	SET_CREDENTIALS,
	SET_CREDENTIAL_TYPES,
	SET_MANAGEMENT,
	SET_TABLE_STATE,
	SET_VENDORS
} from '../reducers/contactsReducer';

export let controller = new AbortController();

const initialState = {
	agencyOrganizations: [],
	contact: null,
	contactTags: [],
	contactList: [],
	contactTypes: [],
	credentialTypes: [],
	managementOrganizations: [],
	tableState: {
		filters: [],
		pageIndex: 0,
		pageSize: 10,
		selectedIds: [],
		sortBy: []
	},
	vendorOrganizations: []
};

// init functions
const setAgencyOrganizations = async dispatch => {
	try {
		const [ { types } ] = await getAgencyOrganizations( controller.signal );

		const typesToMap = types.map( ( {
			organizationID,
			name
		} ) => ( {
			id: organizationID,
			name
		} ) );

		const mappedTypes = mapLookupTypes( typesToMap );

		SET_AGENCIES( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const setContactTags = async dispatch => {
	try {
		const tags = await getContactTags();

		const mappedTags = tags.map( ( {
			id,
			text,
			color
		} ) => ( {
			key: id,
			text,
			value: id,
			color,
			icon: {
				style: { color },
				name: 'tags'
			}
		} ) );

		SET_CONTACT_TAGS( dispatch, controller.signal )( mappedTags );
	} catch ( error ) {
		// handle
	}
};

const setContactTypes = async dispatch => {
	try {
		const [ { types } ] = await getContactTypes( controller.signal );

		const mappedTypes = mapLookupTypes( types );

		SET_CONTACT_TYPES( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const setCredentialTypes = async dispatch => {
	try {
		const [ { types } ] = await getCredentialTypes( controller.signal );

		const mappedTypes = mapLookupTypes( types );

		SET_CREDENTIAL_TYPES( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const setManagementOrganizations = async dispatch => {
	try {
		const [ { types } ] = await getManagementOrganizations( controller.signal );

		const typesToMap = types.map( ( {
			organizationID,
			name
		} ) => ( {
			id: organizationID,
			name
		} ) );

		const mappedTypes = mapLookupTypes( typesToMap );

		SET_MANAGEMENT( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const setVendorOrganizations = async dispatch => {
	try {
		const [ { types } ] = await getVendorOrganizations( controller.signal );

		const typesToMap = types.map( ( {
			organizationID,
			name
		} ) => ( {
			id: organizationID,
			name
		} ) );

		const mappedTypes = mapLookupTypes( typesToMap );

		SET_VENDORS( dispatch, controller.signal )( mappedTypes );
	} catch ( error ) {
		// handle
	}
};

const onInit = async dispatch => {
	controller = new AbortController();

	await setAgencyOrganizations( dispatch );
	await setContactTypes( dispatch );
	await setContactTags( dispatch );
	await setCredentialTypes( dispatch );
	await setManagementOrganizations( dispatch );
	await setVendorOrganizations( dispatch );
};

const onCleanup = () => {
	controller.abort();
};

export const {
	StateContext,
	ActionContext,
	Provider
} = createDataContext( {
	reducer: contactsReducer,
	initialState,
	controller,
	onInit,
	onCleanup
} );

export function useContactState() {
	const state = useContext( StateContext );

	return state;
}

export function useContactAction() {
	const dispatch = useContext( ActionContext );

	const clearContact = useCallback( () => {
		SET_CONTACT( dispatch, controller.signal )( null );
	}, [ dispatch ] );

	const setContact = useCallback( data => {
		SET_CONTACT( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setContactList = useCallback( data => {
		SET_CONTACT_LIST( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setCredentials = useCallback( data => {
		SET_CREDENTIALS( dispatch, controller.signal )( data );
	}, [ dispatch ] );

	const setTags = useCallback( async () => {
		await setContactTags( dispatch, controller.signal );
	}, [ dispatch ] );

	const setTableState = useCallback( updateFunc => {
		SET_TABLE_STATE( dispatch, controller.signal )( updateFunc );
	}, [ dispatch ] );

	const setVendors = useCallback( () => {
		setVendorOrganizations( dispatch, controller.signal );
	}, [ dispatch ] );

	return {
		clearContact,
		setContact,
		setContactList,
		setCredentials,
		setTags,
		setTableState,
		setVendors
	};
}
